"use client";

/**
 * Cards View
 * Renders schedule items grouped by date: an accent day heading, then a responsive grid of cards
 * tinted by each item's first label.
 */

import { useMemo } from "react";

import type { ScheduleItemRow, ScheduleLabelRow } from "../../../../_logic/types/db-types";
import type { ViewerRegistrationKeys } from "../../../../_logic/utils/schedule-registration-match";
import { formatScheduleDayHeading } from "../../../../_logic/utils/schedule-display-format";
import { useEventToday } from "../../../../_hooks/useEventToday";
import { useScheduleLiveMarkers } from "../../../../_hooks/useScheduleLiveMarkers";
import { ScheduleCardTile } from "./ScheduleCardTile";
import type { ScheduleRowConfig } from "./ScheduleRow";

const NO_ITEMS: ScheduleItemRow[] = [];

interface CardsViewProps {
  dateGroups: { date: string | null; items: ScheduleItemRow[] }[];
  labels: ScheduleLabelRow[];
  /** Pre-computed label lookup map (computed once in ScheduleRenderer) */
  labelMap: Map<number, ScheduleLabelRow>;
  primaryColor: string;
  onDelete?: (item: ScheduleItemRow) => void;
  /** When provided, items become clickable (used in public view for detail modal) */
  onItemClick?: (item: ScheduleItemRow) => void;
  /** Page-wide IANA timezone. NULL = floating time. */
  timeZone: string | null;
  isPublic?: boolean;
  /** Viewer's confirmed registrations. `null` = no logged-in attendee. */
  viewerRegistrations: ViewerRegistrationKeys | null;
}

/** Renders schedule items as a responsive grid of cards grouped by date */
export function CardsView({
  dateGroups,
  labels,
  labelMap,
  primaryColor,
  onDelete,
  onItemClick,
  timeZone,
  isPublic = false,
  viewerRegistrations,
}: CardsViewProps) {
  // Hooks
  const todayStr = useEventToday(timeZone);

  // Derived values
  const todayItems = useMemo(
    () => dateGroups.find((g) => g.date !== null && g.date === todayStr)?.items ?? NO_ITEMS,
    [dateGroups, todayStr]
  );
  const { liveItemIds, upcomingMinutesById } = useScheduleLiveMarkers(todayItems, timeZone);
  const cardConfig = useMemo<ScheduleRowConfig>(
    () => ({ labels, primaryColor, onDelete, onItemClick, isPublic, viewerRegistrations, surface: "card" }),
    [labels, primaryColor, onDelete, onItemClick, isPublic, viewerRegistrations]
  );
  const gridClass = isPublic ? "sm:grid-cols-2 md:grid-cols-3" : "@sm:grid-cols-2 @md:grid-cols-3";

  // Render
  return (
    <div className="flex flex-col gap-12">
      {dateGroups.map((group) => (
        <section
          key={group.date ?? "no-date"}
          className="flex flex-col gap-4"
          aria-label={group.date ? formatScheduleDayHeading(group.date) : "Anytime"}
        >
          <h3 className="text-2xl font-bold tracking-tight text-(--lp-accent,#4338ca)">
            {group.date ? formatScheduleDayHeading(group.date) : "Anytime"}
          </h3>
          <ul className={`grid list-none gap-3 ${gridClass}`}>
            {group.items.map((item) => (
              <li key={item.id}>
                <ScheduleCardTile
                  config={cardConfig}
                  labelMap={labelMap}
                  item={item}
                  isLive={liveItemIds.has(item.id)}
                  startsInMinutes={upcomingMinutesById.get(item.id)}
                />
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
